import React, { useState, useCallback } from 'react';
import { Box, Container } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { LoadingSpinner, PageTitleBar } from '@/components';
import FilterModuleRangeInterval from '@/components/filters/FilterModuleRangeInterval';
import { notificationService } from '@core/notifications/NotificationService';
import { getMenuData } from '@core/data/menuData';
import WcnApexchart from '@/components/charts/WcnApexchart';
import AppCard from '@/components/appcard/AppCard';
import { zoneTrackerService } from '@/services/zoneTrackerService';
import { FCSecurePage } from '@/components/shared/navigation/securePage.ts';

interface FilterArgs {
    deviceId: string;
    moduleId: string;
    start: string;
    end: string;
    interval: string;
}

const ZoneTrackerDwellTime: FCSecurePage = () => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [series, setSeries] = useState<any[]>([]);

    const totalSecondsToTime = (totalSeconds: number): string => {
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds - minutes * 60;
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    const formatRangeLabel = useCallback(
        (range: string): string => {
            if (!range.includes(' - ')) return range;
            const split = range.split(' - ');
            const segmentStart = Number.parseInt(split[0], 10);
            const segmentEnd = Number.parseInt(split[1], 10);
            if (Number.isNaN(segmentStart) || Number.isNaN(segmentEnd)) return range;
            return segmentEnd === 10800
                ? `> ${totalSecondsToTime(segmentStart - 1)} ${t('minutes')}`
                : `${totalSecondsToTime(segmentStart)} - ${totalSecondsToTime(segmentEnd)} ${t('minutes')}`;
        },
        [t]
    );

    const loadData = useCallback(
        async (args: FilterArgs) => {
            setLoading(true);
            setSeries([]);

            try {
                const data = await zoneTrackerService.loadDwellTime(
                    args.deviceId,
                    args.moduleId,
                    args.start,
                    args.end,
                    args.interval
                );

                const newSeries = [
                    {
                        name: t('count'),
                        data: data.map((element) => ({
                            x: formatRangeLabel(element.range),
                            y: element.count
                        }))
                    }
                ];

                setSeries(newSeries);
            } catch (error) {
                console.error('Failed to load zone tracker dwell time:', error);
                notificationService.error(t('errorLoadingData'));
            } finally {
                setLoading(false);
            }
        },
        [t, formatRangeLabel]
    );

    return (
        <Box sx={{ width: '100%', minHeight: '100%', bgcolor: 'background.default' }}>
            <PageTitleBar
                menus={getMenuData()}
                currentRoute={{
                    path: '/zonetracker/dwelltime',
                    meta: {
                        breadcrumb: {
                            breadcrumbInactive: 'Statistics / Zone Tracker',
                            breadcrumbActive: 'Dwell Time'
                        }
                    }
                }}
                t={t}
                titleSlot={`${t('zoneTracker')} ${t('dwellTime')}`}
            />
            <Container maxWidth={false} sx={{ py: 3 }}>
                <FilterModuleRangeInterval
                    onLoadData={loadData}
                    moduleType="zoneTracker"
                    intervals={['1:30']}
                    defaultInterval="1:30"
                />

                {loading ? (
                    <LoadingSpinner showBackground={false} />
                ) : (
                    <AppCard
                        heading={t('dwellTime')}
                        icon="border_all"
                        iconClass=""
                        colClasses="flex col-12"
                        customClasses="mb-0"
                        fullBlock={false}
                        footer={false}
                    >
                        <WcnApexchart
                            type="bar"
                            chartId="zoneTrackerDwellTime"
                            series={series}
                            height={550}
                            xAxisType="category"
                            xAxisShowTime={true}
                        />
                    </AppCard>
                )}
            </Container>
        </Box>
    );
};

ZoneTrackerDwellTime.displayName = 'ZoneTrackerDwellTime';

export default ZoneTrackerDwellTime;
